/** Add branded photography — each item needs unique `id`, descriptive `alt`, and dimensions for CLS. */

export type GalleryItem = {
  id: string
  /** Descriptive alt for SEO/a11y */
  alt: string
  /** Fixed dimensions help CLS */
  src: string
  width: number
  height: number
  badge?: string
}

/** Portrait nail photography — assumed dimensions for layout/CLS. */
const PORTRAIT_W = 800
const PORTRAIT_H = 1000

export const galleryItems: GalleryItem[] = [
  {
    id: 'art-01',
    src: '/arts/1.jpg',
    alt: 'Almond manicure with cream, olive, and sage tones featuring delicate gold botanical accents',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-02',
    src: '/arts/2.jpg',
    alt: 'Soft sheer nude nails with hand-painted daisies and gold leaf detailing',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-03',
    src: '/arts/3.jpg',
    alt: 'Olive green manicure with a single floral accent nail on a knit backdrop',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-04',
    src: '/arts/4.jpg',
    alt: 'Glossy almond nails with botanical art and warm cream undertones',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-05',
    src: '/arts/5.jpg',
    alt: 'Editorial manicure with daisy art, gold flecks, and a soft floral backdrop',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-06',
    src: '/arts/6.jpg',
    alt: 'Matte sage and cream nail set with painted vine detail and gold ring styling',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-07',
    src: '/arts/7.jpg',
    alt: 'Modern almond manicure with a translucent base and minimalist floral accent',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-08',
    src: '/arts/8.jpg',
    alt: 'Soft pink manicure with delicate gold filigree and floral hand-painted detail',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-09',
    src: '/arts/9.jpg',
    alt: 'Olive and ivory manicure with botanical leaf art and a warm chunky knit backdrop',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-10',
    src: '/arts/10.jpg',
    alt: 'Editorial nail close-up with cream, sage, and gold botanical accent design',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
    badge: 'Signature',
  },
  {
    id: 'art-11',
    src: '/arts/11.jpg',
    alt: 'Pastel yellow almond nails with textured daisy nail art and blue floral embroidery on white linen',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-12',
    src: '/arts/12.jpg',
    alt: 'Bright yellow French tips with hand-painted sunflower accent nails, stacked gold rings, cream knit',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-13',
    src: '/arts/13.jpg',
    alt: 'Long almond nails with white blossoms, green leaves, and gold bead accents against soft garden bokeh',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-14',
    src: '/arts/14.jpg',
    alt: 'Almond nails with red florals, gold vines, lotus detail, and warm amber tortoiseshell tips',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-15',
    src: '/arts/15.jpg',
    alt: 'Bridal almond nails with white florals, burgundy vines, and gold foil on lace and pearl styling',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-16',
    src: '/arts/16.jpg',
    alt: 'Ivory nails with vintage script and sepia florals, gold initial and heart charms on delicate lace',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-17',
    src: '/arts/17.jpg',
    alt: 'Watercolor green leaf nail art with gold glitter lines, hand posed with a matching fresh leaf',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-18',
    src: '/arts/18.jpg',
    alt: 'Matte navy and nude manicure with peach florals, botanical line art, and gold metallic accents',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-19',
    src: '/arts/19.jpg',
    alt: 'Long coffin nails in creamy beige with navy floral art and raised gold stud details',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-20',
    src: '/arts/20.jpg',
    alt: 'Cream glossy nails with navy and dusty blue watercolor florals and gold crystal accents',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-21',
    src: '/arts/21.jpg',
    alt: 'Elegant nail art showcase with soft neutral base and detailed accent design',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-22',
    src: '/arts/22.jpg',
    alt: 'Long-shaped manicure with rich color and refined salon-finish shine',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-23',
    src: '/arts/23.jpg',
    alt: 'Almond nail set with hand-drawn art and balanced negative space',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-24',
    src: '/arts/24.jpg',
    alt: 'Glossy nails with floral-inspired details and warm skin-tone styling',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-25',
    src: '/arts/25.jpg',
    alt: 'Editorial close-up of manicured nails with subtle metallic highlights',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-26',
    src: '/arts/26.jpg',
    alt: 'Soft ombré or gradient nail look with clean cuticle line and high shine',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-27',
    src: '/arts/27.jpg',
    alt: 'Statement accent nails with fine-line art and polished finish',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-28',
    src: '/arts/28.jpg',
    alt: 'Natural-length nails with balanced color blocking and elegant proportions',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-29',
    src: '/arts/29.jpg',
    alt: 'Bridal or special-occasion manicure with delicate detail and soft palette',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
  {
    id: 'art-30',
    src: '/arts/30.jpg',
    alt: 'Full-set nail art with cohesive theme and professional gel or polish finish',
    width: PORTRAIT_W,
    height: PORTRAIT_H,
  },
]

/** Home page gallery preview — curated subset in editorial order (full gallery has more). */
export const galleryPreviewItems: GalleryItem[] = [
  galleryItems[0]!,
  galleryItems[4]!,
  galleryItems[1]!,
  galleryItems[5]!,
  galleryItems[2]!,
  galleryItems[7]!,
  galleryItems[3]!,
  galleryItems[6]!,
  galleryItems[8]!,
  galleryItems[9]!,
]
